import { Link, Outlet, useNavigate, useParams } from 'react-router-dom';
import { RiArrowLeftSLine } from 'react-icons/ri';
import { BsPlusLg } from 'react-icons/bs';
import ReactPaginate from 'react-paginate';

import Box from 'components/ui/box';
import { useAuth } from 'context/AuthContext';
import InputMask from 'react-input-mask';
import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { getEquieGestor } from 'services/dashboard.service';
import ArticlePlaceholder from 'components/ui/article-placeholder';
import Avatar from 'funifier-nice-avatar';
import { TbSearch } from 'react-icons/tb';

import './styles.scss';
import { IoIosArrowBack, IoIosArrowForward } from 'react-icons/io';
import { MinhaEquipeDetail } from './components/detail';
import Button from 'components/ui/button';
import SearchGestor from './components/SearchGestor';
import Header from './components/Header';
import AddEquipe from './components/AddEquipe';
import { FlowNoAccess } from './ApreciarRealizacao/shared/FlowLoading';
import { IoPersonAdd } from 'react-icons/io5';

export default function MinhaEquipe() {
  const params = useParams();
  const navigate = useNavigate();

  const [list, setList] = useState<unknown[]>();
  const [page, setPage] = useState(0);
  const [activePag, setActivePag] = useState(0);
  const [showDetail, setShowDetail] = useState('');
  const [id, setId] = useState<any>(false);
  const [showModal, setShowModal] = useState(false);

  const [mask] = useState('');

  const searchRef = useRef<any>();

  const [loadingList, setLoadingList] = useState(true);

  const { user, userInfo } = useAuth();

  const isRootUser = useMemo(() => {
    return typeof id?.extra?.id === 'string' ? false : true;
  }, [id?.extra?.id]);

  function handleDetail(item: any) {
    setShowDetail(item);
  }

  const loadData = useCallback(
    async (e?: any, itemPage?: number) => {
      if (!user) return;

      const payload = id?.extra?.id ? id.extra.id : user?.extra.id;

      if (e) {
        e.preventDefault();
      }

      try {
        const rangeItem = itemPage ? itemPage * 20 : 0;

        setActivePag(itemPage ? itemPage : 0);
        setLoadingList(true);
        setList([]);

        const homeSection = document.getElementById('main-body');
        if (homeSection) {
          homeSection.scrollTop = 0;
        }

        const data = await getEquieGestor(payload, searchRef.current.value, rangeItem);
        setPage(data.range.pages);

        setList(
          data.result.filter((x: { _id: any }) => {
            return x._id !== user?.extra.id;
          }),
        );

        setLoadingList(false);
      } catch (e) {}
    },
    [id?.extra?.id, user],
  );

  useEffect(() => {
    loadData();
  }, [userInfo, id, loadData]);

  if (!userInfo) return <></>;

  // Não gestor (ex.: assessora substituta sem nível próprio) chegava aqui pela URL e via a página
  // em branco; mostra o mesmo "sem acesso" do gate da apreciação, com o "Voltar" para a home (HOM-06).
  if (!userInfo.is_manager) return <FlowNoAccess backTo="/" />;

  if (showDetail) return <MinhaEquipeDetail onClose={() => setShowDetail('')} id={showDetail} isFromTeam={!id} />;

  return (
    <>
      <main className="mx-auto container p-3 lg:p-10 overflow-hidden" aria-busy={loadingList}>
        <Header
          isRoot={isRootUser}
          onSubmitSearch={function (id: any): void {
            setId(id);
          }}
          title={!isRootUser ? `Equipe de ${id?.extra?.nome_guerra}` : null}
        />
        <h1 className="sr-only">Minha Equipe</h1>
        <div className="mb-5 lg:flex items-center justify-between">
          <p className="text-lg mb-4 lg:mb-0" tabIndex={0}>
            Lista de funcionários sob sua supervisão <span className="text-yellow">({list?.length || 0})</span>
          </p>

          <div className="space-x-4 flex flex-row items-center justify-between">
            <div className="space-x-4 flex flex-row items-center justify-between">
              <form onSubmit={e => loadData(e)} className="pl-1 -ml-1 flex-1">
                <InputMask
                  placeholder="Pesquise com nome e chave"
                  alwaysShowMask={false}
                  mask={mask}
                  ref={(ref: any) => {
                    searchRef.current = ref;
                  }}
                  inputMode="search"
                  style={{ fontSize: 16 }}
                  className="min-w-full lg:min-w-[416px] bg-maastricht-blue text-sm text-white font-thin flex-1 rounded-md py-2 px-4"
                  aria-label="Pesquisar funcionário por nome ou chave"
                />
              </form>

              <TbSearch
                size={28}
                className="cursor-pointer hover:ring-2 mr-1 rounded-full focus-visible:outline focus-visible:outline-2 focus-visible:outline-yellow"
                onClick={() => loadData(null)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    loadData(null);
                  }
                }}
                role="button"
                aria-label="Buscar funcionário"
                tabIndex={0}
              />
            </div>

            <div>
              <button
                onClick={() => setShowModal(true)}
                className="space-x-2 flex items-center text-electric-blue text-base lg:text-md uppercase hover:no-underline hover:text-white active:no-underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-yellow"
                aria-label="Adicionar funcionário à equipe"
              >
                <span>Adicionar Funcionário</span>
                <IoPersonAdd />
              </button>
            </div>
          </div>
        </div>

        <div>
          <Box>
            <div className="space-y-5 lg:p-3">
              {loadingList && (
                <div className="space-y-2">
                  <ArticlePlaceholder />
                  <ArticlePlaceholder />
                </div>
              )}

              {!loadingList && list?.length === 0 && (
                <div className="text-center" role="alert">
                  <h3 className="text-xl" tabIndex={0}>Nenhum resultado encontrado</h3>
                </div>
              )}

              <div className="space-y-2">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  {list?.map((item: any) => (
                    <article
                      className={`bg-maastricht-blue rounded-xl text-white space-y-2 cursor-pointer overflow-hidden hover:ring-2`}
                      key={item?._id}
                      onClick={() => handleDetail(item.matricula)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handleDetail(item.matricula);
                        }
                      }}
                      role="button"
                      aria-label={`Funcionário: ${item.nome}, função: ${item.funcao}, matrícula: ${item.matricula}`}
                      tabIndex={0}
                    >
                      <div className="px-4">
                        <div className="flex flex-col items-center justify-around md:h-40 space-y-1 text-center">
                          <div className="w-28 h-28 overflow-hidden rounded-full bg-purple text-3xl font-bold flex flex-row items-center justify-center">
                            {!!item.avatar_type &&
                              (item?.image?.medium?.url ? (
                                <img src={item.image.medium.url} alt={`Avatar de ${item.nome}`} className="w-full h-full" />
                              ) : (
                                item.nome[0]
                              ))}
                            {!item.avatar_type &&
                              (!!item.avatar ? <Avatar className="w-full h-full" {...item.avatar} /> : item.nome[0])}
                          </div>
                        </div>
                      </div>
                      <div className="bg-prussian-blue-3 w-full h-full text-center pb-7 pt-5">
                        <div className="font-bold">{item.nome}</div>
                        <div className="mt-1">
                          <span className="text-electric-blue font-thin">{item.diretoria} </span>
                          <span className="font-thin">- {item.funcao}</span>
                        </div>
                        <div className="text-electric-blue  mb-3">
                          <span className="font-thin">{item.matricula}</span>
                        </div>
                        <button className="mt-4 focus-visible:outline focus-visible:outline-2 focus-visible:outline-yellow" aria-label={`Ver mais detalhes de ${item.nome}`}> 
                          <BsPlusLg className="mr-2 inline-block" size={17} color="#C2D2FF" />
                          <span className="uppercase">Ver mais</span>
                        </button>
                      </div>
                    </article>
                  ))}
                </div>
              </div>
            </div>

            <div className={`flex justify-center ${page < 2 && 'hidden'}`}>
              <nav aria-label="Paginação de funcionários">
                <ReactPaginate
                  activeClassName={'item active '}
                  breakClassName={'item break-me '}
                  breakLabel={'...'}
                  containerClassName={'pagination'}
                  disabledClassName={'disabled-page'}
                  nextClassName={'item next '}
                  pageClassName={'item pagination-page '}
                  previousClassName={'item previous'}
                  initialPage={0}
                  forcePage={activePag}
                  nextLabel={<><IoIosArrowForward style={{ fontSize: 18, width: 150 }} aria-hidden="true" /><span className="sr-only">Próxima página</span></>}
                  onPageChange={data => loadData(null, data.selected)}
                  pageRangeDisplayed={3}
                  pageCount={page}
                  previousLabel={<><IoIosArrowBack style={{ fontSize: 18, width: 150 }} aria-hidden="true" /><span className="sr-only">Página anterior</span></>}
                  ariaLabelBuilder={(pageIndex) => `Ir para a página ${pageIndex}`}
                />
              </nav>
            </div>
          </Box>
        </div>
      </main>

      {showModal && (
        <AddEquipe
          closeModal={() => setShowModal(false)}
          onSubmit={() => {
            setShowModal(false);
            loadData();
          }}
        />
      )}

      <Outlet />
    </>
  );
}
